<script setup lang="ts">
import { usePagination, useSelectableRows } from '@/app/core/composable/tables'
import { onMounted, ref, watch } from 'vue'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronLeftDoubleIcon,
  ChevronRightDoubleIcon,
  DTextField,
  DBtn
} from 'front-library'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Columns {
  title: string
  key: string
  align?: string
}
const props = withDefaults(
  defineProps<{
    rows: any[]
    columns: Columns[]
    classTable: string
    rowsPerPage?: number[]
    perPage?: number
    selectRowsProp?: string
    heightTable?: string
  }>(),
  {
    rows: () => [],
    columns: () => [],
    rowsPerPage: () => [10, 25, 50, 200],
    perPage: 10,
    selectRowsProp: 'id',
    heightTable: ''
  }
)

const emit = defineEmits(['addAverage'])

const searchText = ref('')
const perPageNew = ref(props.perPage)

const {
  currentPageRows,
  updateRows,
  currentPage,
  totalPages,
  nextPage,
  prevPage,
  goToFirsPage,
  goToLastPage,
  search,
  setConfig
} = usePagination<any>(props.rows)

const {
  selectedRows,
  selectedAllRows,
  toggleRow,
  toggleAllRow,
  setSelectProp,
  setRows
} = useSelectableRows<any>(props.rows)

onMounted(() => {
  setSelectProp(props.selectRowsProp)
})

watch(
  () => props.rows.length,
  () => {
    updateRows(props.rows)
    setRows(props.rows)
  }
)

defineExpose({
  toggleRow,
  toggleAllRow,
  selectedRows,
  selectedAllRows
})
</script>
<template>
  <div class="h-full w-full">
    <div class="flex pb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
      <DTextField
        :placeholder="t('form.button.search') + '...'"
        :icon="'SearchLgIcon'"
        class="mr-2 w-full"
        v-model="searchText"
        @keyup.enter="search(searchText)" />
      <DBtn
        @click.prevent="search(searchText)"
        v-html="t('form.button.search')" />
    </div>
    <div
      class="w-full h-auto overflow-y-auto scroll"
      :style="{ maxHeight: heightTable }">
      <table :class="classTable" class="min-w-full table-auto">
        <thead class="h-10 sticky top-0 z-10">
          <tr class="bg-white">
            <th
              :id="column.key"
              v-for="column in columns"
              v-bind:key="column.key"
              class="px-3">
              <div
                class="flex items-center justify-start text-base text-gray-600 whitespace-nowrap">
                {{ column.title }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="bg-gray-50">
          <tr
            v-for="(row, i) in currentPageRows"
            :key="i"
            class="border-y-2 border-gray-100 h-tr bg-white hover:bg-gray-100">
            <slot name="row" :row="row" :columns="columns">
              <td
                v-for="column in columns"
                v-bind:key="column.key"
                class="px-3">
                <slot
                  :name="
                    typeof row[column.key] == 'undefined' ? column.key : ''
                  "
                  v-bind="row"></slot>
                <div
                  class="max-w-xs overflow-hidden whitespace-nowrap text-ellipsis">
                  {{ row[column.key] }}
                </div>
              </td>
            </slot>
          </tr>
          <tr>
            <td
              class="bg-white py-5 text-center"
              v-if="currentPageRows.length == 0"
              :colspan="columns.length + 1">
              <h1>{{ t('table.noData') }}</h1>
            </td>
          </tr>
        </tbody>
        <tfoot class="h-12 bg-white sticky bottom-0 shadow-md">
          <tr>
            <td :colspan="columns.length + 1">
              <div class="flex w-full justify-end px-4">
                <div class="flex items-center gap-4 text-sm">
                  <div class="flex gap-2">
                    <label>{{ t('table.rowsPerPage') }}</label>
                    <select
                      v-model="perPageNew"
                      @change="setConfig(perPageNew, searchText)">
                      <option
                        v-for="(item, i) in rowsPerPage"
                        :key="i"
                        :value="item">
                        {{ item }}
                      </option>
                    </select>
                  </div>
                  <span> Pag. {{ currentPage }} de {{ totalPages }} </span>
                  <div class="flex">
                    <ChevronLeftDoubleIcon
                      @click="goToFirsPage"
                      :class="[
                        currentPage == 1 ? 'text-gray-300' : 'text-gray-600',
                        'cursor-pointer h-6 w-6'
                      ]" />
                    <ChevronLeftIcon
                      @click="prevPage"
                      :class="[
                        currentPage == 1 ? 'text-gray-300' : 'text-gray-600',
                        'cursor-pointer h-6 w-6'
                      ]" />
                    <ChevronRightIcon
                      @click="nextPage"
                      :class="[
                        currentPage == totalPages
                          ? 'text-gray-300'
                          : 'text-gray-600',
                        'cursor-pointer h-6 w-6'
                      ]" />
                    <ChevronRightDoubleIcon
                      @click="goToLastPage"
                      :class="[
                        currentPage == totalPages
                          ? 'text-gray-300'
                          : 'text-gray-600',
                        'cursor-pointer h-6 w-6'
                      ]" />
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>
<style scoped>
.h-tr {
  height: 2.5rem;
}
table.tableLiquidation td:first-child {
  width: 50%;
}
</style>
